<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  maxButtons: {
    type: Number,
    default: 3,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const buttons = computed(() => props.modelValue || []);

const buttonTypeOptions = computed(() => [
  {
    label: t('WHATSAPP.BUTTON_BUILDER.TYPE_QUICK_REPLY'),
    value: 'quick_reply',
  },
  { label: t('WHATSAPP.BUTTON_BUILDER.TYPE_URL'), value: 'url' },
  {
    label: t('WHATSAPP.BUTTON_BUILDER.TYPE_PHONE_NUMBER'),
    value: 'phone_number',
  },
]);

const canAddMore = computed(() => buttons.value.length < props.maxButtons);

const emitButtons = nextButtons => emit('update:modelValue', nextButtons);

const addButton = () => {
  if (!canAddMore.value) return;

  emitButtons([
    ...buttons.value,
    {
      id: `btn_${Date.now()}`,
      type: 'quick_reply',
      text: '',
      url: '',
      phone_number: '',
    },
  ]);
};

const removeButton = index => {
  emitButtons(buttons.value.filter((_, buttonIndex) => buttonIndex !== index));
};

const updateButton = (index, field, value) => {
  emitButtons(
    buttons.value.map((button, buttonIndex) => {
      if (buttonIndex !== index) return button;

      const nextButton = { ...button, [field]: value };

      if (field === 'type') {
        if (value !== 'url') nextButton.url = '';
        if (value !== 'phone_number') nextButton.phone_number = '';
      }

      return nextButton;
    })
  );
};

const getButtonTypeLabel = type => {
  switch (type) {
    case 'url':
      return t('WHATSAPP.BUTTON_BUILDER.TYPE_URL');
    case 'phone_number':
      return t('WHATSAPP.BUTTON_BUILDER.TYPE_PHONE_NUMBER');
    default:
      return t('WHATSAPP.BUTTON_BUILDER.TYPE_QUICK_REPLY');
  }
};

const getButtonIcon = type => {
  switch (type) {
    case 'url':
      return 'link';
    case 'phone_number':
      return 'call';
    default:
      return 'arrow-reply';
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-3">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('WHATSAPP.BUTTON_BUILDER.TITLE') }}
      </p>
      <Button
        v-if="canAddMore"
        :label="$t('WHATSAPP.BUTTON_BUILDER.ADD_BUTTON')"
        size="sm"
        @click="addButton"
      >
        <template #icon>
          <DashboardIcon icon="add" size="16" />
        </template>
      </Button>
    </div>

    <p v-if="buttons.length === 0" class="mb-0 text-sm text-n-slate-11">
      {{ $t('WHATSAPP.BUTTON_BUILDER.NO_BUTTONS') }}
    </p>

    <div
      v-for="(button, index) in buttons"
      :key="button.id || index"
      class="flex flex-col gap-2 rounded-lg bg-n-alpha-2 p-3"
    >
      <div class="grid items-center gap-3 md:grid-cols-[180px_1fr_auto]">
        <div class="flex items-center gap-2">
          <DashboardIcon
            :icon="getButtonIcon(button.type)"
            size="16"
            class="text-n-slate-11"
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{
              $t('WHATSAPP.BUTTON_BUILDER.BUTTON_LABEL', { index: index + 1 })
            }}
          </span>
        </div>
        <span class="hidden text-sm font-medium text-n-slate-12 md:block">
          {{ $t('WHATSAPP.BUTTON_BUILDER.BUTTON_TEXT') }}
        </span>
        <Button
          icon="i-lucide-trash-2"
          size="sm"
          color="ruby"
          variant="ghost"
          class="justify-self-end"
          @click="removeButton(index)"
        />
      </div>

      <div class="grid gap-3 md:grid-cols-[180px_1fr]">
        <SelectMenu
          :model-value="button.type"
          :options="buttonTypeOptions"
          :label="getButtonTypeLabel(button.type)"
          sub-menu-position="bottom"
          @update:model-value="updateButton(index, 'type', $event)"
        />

        <Input
          :model-value="button.text"
          :placeholder="$t('WHATSAPP.BUTTON_BUILDER.BUTTON_TEXT_PLACEHOLDER')"
          maxlength="20"
          @update:model-value="updateButton(index, 'text', $event)"
        />
      </div>

      <Input
        v-if="button.type === 'url'"
        :model-value="button.url"
        :label="$t('WHATSAPP.BUTTON_BUILDER.URL_LABEL')"
        :placeholder="$t('WHATSAPP.BUTTON_BUILDER.URL_PLACEHOLDER')"
        @update:model-value="updateButton(index, 'url', $event)"
      />

      <Input
        v-if="button.type === 'phone_number'"
        :model-value="button.phone_number"
        :label="$t('WHATSAPP.BUTTON_BUILDER.PHONE_LABEL')"
        :placeholder="$t('WHATSAPP.BUTTON_BUILDER.PHONE_PLACEHOLDER')"
        @update:model-value="updateButton(index, 'phone_number', $event)"
      />
    </div>

    <p v-if="buttons.length > 0" class="mb-0 text-xs text-n-slate-11">
      {{
        $t('WHATSAPP.BUTTON_BUILDER.BUTTONS_COUNT', {
          count: buttons.length,
          max: maxButtons,
        })
      }}
    </p>
  </div>
</template>
